import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API, useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { Plus, Edit, Trash2, X, Save, Calendar } from 'lucide-react';

export default function ManageSessions() {
  const { token } = useAuth();
  const { currentColors } = useTheme();
  const [sessions, setSessions] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    title: '', description: '', instructor: '', duration: 60, session_type: 'individual', image_url: ''
  });

  useEffect(() => { loadSessions(); }, []);

  const loadSessions = async () => {
    try {
      const response = await axios.get(`${API}/healing-sessions`);
      setSessions(response.data);
    } catch (error) { console.error('Failed to load sessions:', error); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await axios.put(`${API}/healing-sessions/${editingId}`, formData, { headers: { Authorization: `Bearer ${token}` } });
      } else {
        await axios.post(`${API}/healing-sessions`, formData, { headers: { Authorization: `Bearer ${token}` } });
      }
      resetForm();
      loadSessions();
    } catch (error) { alert('Failed to save session'); }
  };

  const handleEdit = (session) => {
    setFormData({
      title: session.title, description: session.description, instructor: session.instructor,
      duration: session.duration, session_type: session.session_type, image_url: session.image_url || ''
    });
    setEditingId(session.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this session?')) return;
    try {
      await axios.delete(`${API}/healing-sessions/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      loadSessions();
    } catch (error) { alert('Failed to delete'); }
  };

  const resetForm = () => {
    setFormData({ title: '', description: '', instructor: '', duration: 60, session_type: 'individual', image_url: '' });
    setEditingId(null);
    setShowForm(false);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-3">
          <Calendar className="w-6 h-6" style={{ color: currentColors.primary }} />
          <h2 className="text-2xl font-cinzel" style={{ color: currentColors.primary }}>Manage Sessions</h2>
        </div>
        <button onClick={() => setShowForm(!showForm)} className="cosmic-btn flex items-center gap-2">
          {showForm ? <X className="w-5 h-5" /> : <Plus className="w-5 h-5" />}
          <span>{showForm ? 'Cancel' : 'Add New'}</span>
        </button>
      </div>

      {showForm && (
        <div className="glass-card p-6">
          <h3 className="text-xl font-cinzel mb-4" style={{ color: currentColors.text }}>{editingId ? 'Edit' : 'Add'} Session</h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm mb-1" style={{ color: currentColors.textMuted }}>Title *</label>
                <input type="text" value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} className="cosmic-input w-full" required />
              </div>
              <div>
                <label className="block text-sm mb-1" style={{ color: currentColors.textMuted }}>Instructor *</label>
                <input type="text" value={formData.instructor} onChange={(e) => setFormData({ ...formData, instructor: e.target.value })} className="cosmic-input w-full" required />
              </div>
            </div>
            <div>
              <label className="block text-sm mb-1" style={{ color: currentColors.textMuted }}>Description *</label>
              <textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} className="cosmic-input w-full" rows="3" required />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm mb-1" style={{ color: currentColors.textMuted }}>Duration (min)</label>
                <input type="number" value={formData.duration} onChange={(e) => setFormData({ ...formData, duration: parseInt(e.target.value) })} className="cosmic-input w-full" min="15" />
              </div>
              <div>
                <label className="block text-sm mb-1" style={{ color: currentColors.textMuted }}>Type</label>
                <select value={formData.session_type} onChange={(e) => setFormData({ ...formData, session_type: e.target.value })} className="cosmic-input w-full">
                  <option value="individual">Individual</option>
                  <option value="group">Group</option>
                </select>
              </div>
              <div>
                <label className="block text-sm mb-1" style={{ color: currentColors.textMuted }}>Image URL</label>
                <input type="url" value={formData.image_url} onChange={(e) => setFormData({ ...formData, image_url: e.target.value })} className="cosmic-input w-full" />
              </div>
            </div>
            <button type="submit" className="cosmic-btn w-full flex items-center justify-center gap-2">
              <Save className="w-5 h-5" /><span>{editingId ? 'Update' : 'Create'} Session</span>
            </button>
          </form>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {sessions.map((session) => (
          <div key={session.id} className="glass-card overflow-hidden">
            {session.image_url && <img src={session.image_url} alt={session.title} className="w-full h-32 object-cover" />}
            <div className="p-4">
              <h3 style={{ color: currentColors.text }} className="font-cinzel mb-1">{session.title}</h3>
              <p className="text-sm mb-2" style={{ color: currentColors.textMuted }}>{session.description}</p>
              <p className="text-xs mb-2" style={{ color: currentColors.accent }}>by {session.instructor} • {session.duration} min</p>
              <div className="flex items-center justify-between">
                <span className="text-xs px-2 py-1 rounded-full capitalize" style={{ backgroundColor: `${currentColors.secondary}20`, color: currentColors.secondary }}>{session.session_type}</span>
                <div className="flex gap-1">
                  <button onClick={() => handleEdit(session)} className="p-1.5 rounded" style={{ color: currentColors.accent }}><Edit className="w-4 h-4" /></button>
                  <button onClick={() => handleDelete(session.id)} className="p-1.5 rounded text-red-400"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            </div>
          </div>
        ))}
        {sessions.length === 0 && (
          <div className="col-span-full text-center py-8" style={{ color: currentColors.textMuted }}>No sessions found.</div>
        )}
      </div>
    </div>
  );
}
